import { useState } from "react";
import type { ReviewFlow, ReviewFlowInput, ReviewItem } from "../../../types/reviews";

const CAUSE_OPTIONS = ["品質不良", "サイズ", "説明・写真と差異", "配送・梱包", "使い方"];
const RESPONSE_OPTIONS = ["交換", "返金", "連絡・説明のみ", "対応不要"];
const IMPROVEMENT_OPTIONS = ["商品ページ修正", "仕入先・製造へ連絡", "梱包・出荷改善", "改善不要"];

interface FlowTimelineProps {
  item: ReviewItem;
  owners: string[];
  onSave: (input: ReviewFlowInput) => Promise<void>;
  busy: boolean;
}

function ChipGroup({
  options,
  value,
  onSelect,
  disabled,
}: {
  options: string[];
  value: string | null;
  onSelect: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="rv-chip-group">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`rv-chip ${value === opt ? "selected" : ""}`}
          disabled={disabled}
          onClick={() => onSelect(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export function FlowTimeline({ item, owners, onSave, busy }: FlowTimelineProps) {
  const flow = item.flow as ReviewFlow;
  const [responseBody, setResponseBody] = useState(flow.responseBody ?? "");
  const [memo, setMemo] = useState(flow.memo ?? "");

  const stageOrder = ["cause", "resp", "improve", "ready", "done"];
  const stageIndex = stageOrder.indexOf(flow.stage);

  const dotClass = (idx: number) => (idx < stageIndex || flow.stage === "done" ? "done" : idx === stageIndex ? "current" : "");
  const canComplete = !!flow.cause && !!flow.responseType && !!flow.improvement && flow.stage !== "done";

  // 一次返信は「実際に投稿済み」の時だけ完了扱い（下書き・投稿待ちを送信済みと表示しない）
  const replyPosted = item.reply.status === "posted";
  const replyLabel = replyPosted
    ? `投稿済み${item.reply.repliedAt ? `（${item.reply.repliedAt.slice(0, 16).replace("T", " ")}）` : ""}`
    : item.reply.status === "queued"
      ? "投稿待ち（翌朝7:40に楽天へ投稿）"
      : item.reply.status === "failed"
        ? `投稿失敗${item.reply.error ? `：${item.reply.error}` : ""}（上の返信欄から再投稿）`
        : "未返信（上の返信欄から返信）";

  return (
    <div className="rv-timeline">
      <div className="rv-timeline-step">
        <span className={`rv-timeline-dot ${replyPosted ? "done" : "current"}`}>{replyPosted ? "✓" : "1"}</span>
        <div className="rv-timeline-body">
          <div className="rv-timeline-title">一次返信（レビューへのコメント）</div>
          <span className="rv-note">{replyLabel}</span>
          {replyPosted && item.reply.body && <div className="rv-detail-body">{item.reply.body}</div>}
        </div>
      </div>

      <div className="rv-timeline-step">
        <span className={`rv-timeline-dot ${dotClass(0)}`}>{stageIndex > 0 || flow.stage === "done" ? "✓" : "2"}</span>
        <div className="rv-timeline-body">
          <div className="rv-timeline-title">原因分類</div>
          <ChipGroup
            options={CAUSE_OPTIONS}
            value={flow.cause}
            disabled={busy || flow.stage === "done"}
            onSelect={(v) => onSave({ cause: v })}
          />
        </div>
      </div>

      <div className="rv-timeline-step">
        <span className={`rv-timeline-dot ${dotClass(1)}`}>{stageIndex > 1 || flow.stage === "done" ? "✓" : "3"}</span>
        <div className="rv-timeline-body">
          <div className="rv-timeline-title">
            <span>顧客への個別対応</span>
            <span className="rv-note">{flow.overdue ? "期限超過" : `期限まで あと${Math.max(0, Math.round(flow.ageHours))}h`}</span>
          </div>
          <ChipGroup
            options={RESPONSE_OPTIONS}
            value={flow.responseType}
            disabled={busy || flow.stage === "done"}
            onSelect={(v) => onSave({ responseType: v })}
          />
          <textarea
            className="rv-textarea"
            style={{ minHeight: 64 }}
            placeholder="個別連絡の内容（メモ用）"
            value={responseBody}
            disabled={flow.stage === "done"}
            onChange={(e) => setResponseBody(e.target.value)}
            onBlur={() => responseBody !== (flow.responseBody ?? "") && onSave({ responseBody })}
          />
        </div>
      </div>

      <div className="rv-timeline-step">
        <span className={`rv-timeline-dot ${dotClass(2)}`}>{stageIndex > 2 || flow.stage === "done" ? "✓" : "4"}</span>
        <div className="rv-timeline-body">
          <div className="rv-timeline-title">再発防止アクション</div>
          <ChipGroup
            options={IMPROVEMENT_OPTIONS}
            value={flow.improvement}
            disabled={busy || flow.stage === "done"}
            onSelect={(v) => onSave({ improvement: v })}
          />
        </div>
      </div>

      <div className="rv-timeline-step">
        <span className={`rv-timeline-dot ${flow.stage === "done" ? "done" : ""}`}>{flow.stage === "done" ? "✓" : "5"}</span>
        <div className="rv-timeline-body">
          <div className="rv-timeline-title">完了</div>
          {flow.stage === "done" ? (
            <span className="rv-tag rv-tag--good">完了済み（{flow.completedAt?.slice(0, 16).replace("T", " ")}）</span>
          ) : (
            <>
              <button className="rv-btn rv-btn-primary" disabled={!canComplete || busy} onClick={() => onSave({ complete: true })}>
                対応を完了する
              </button>
              {!canComplete && <span className="rv-note">原因分類・個別対応・再発防止アクションをすべて入力すると完了できます。</span>}
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="rv-field-label">担当者</span>
        <select
          className="rv-select"
          value={flow.owner ?? owners[0]}
          disabled={busy}
          onChange={(e) => onSave({ owner: e.target.value })}
        >
          {owners.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <span className="rv-field-label">対応メモ</span>
        <textarea
          className="rv-textarea"
          style={{ minHeight: 60 }}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          onBlur={() => memo !== (flow.memo ?? "") && onSave({ memo })}
        />
      </div>
    </div>
  );
}
